/**
 * usePublicMapShellState
 *
 * Map-state plumbing for the public share/embed map shell. Mirrors the
 * authenticated `MapInstance` wiring on top of the hydrated store: terrain
 * mode (2D/3D), stable zoom for geometry renderers, store-driven zoom-to
 * requests (`map.zoomToCoordinates`, fed by go-to-my-location and list rows),
 * artifact visibility gates, and the share-config road-layer allowlist.
 *
 * @module components/share/public/PublicMapShell/hooks/usePublicMapShellState
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { MapRef, ViewStateChangeEvent } from '@vis.gl/react-maplibre';
import type { StyleSpecification } from 'maplibre-gl';

import { useGeoMarkupStore } from '@/store';
import {
  selectArtifactNotesVisible,
  selectArtifactObstructionsVisible,
  selectArtifactTasksVisible,
  selectMapNotes,
} from '@/store/selectors';
import { selectMapTasks } from '@/store/selectors/taskSelectors';
import { useDeviceLayout } from '@/hooks';
import { useTerrainMode } from '@/hooks/useTerrainMode';
import { useMapMarkerHoverPreview } from '@/components/map/hooks/useMapMarkerHoverPreview';
import { useStableZoom } from '@/components/map/hooks/useStableZoom';
import { useZoomToCoordinatesEffect } from '@/components/map/hooks/useZoomToCoordinatesEffect';
import { MAP_CONFIG } from '@/config/maplibre';
import { standardLayerKeys } from '@/config/mapFeatures';
import { clampZoom } from '@/utils/zoomHelpers';
import type { LayerVisibility } from '@/types/layers';
import type { NoteMapItem } from '@/types/notes/NoteMapItem';
import type { Task } from '@/types';
import type { PublicShareConfig } from '@/types/share';

import { useShareBasemapPersistence } from './useShareBasemapPersistence';

/** Stable empty fallbacks so the gated arrays keep a constant reference. */
const EMPTY_TASKS: Task[] = [];
const EMPTY_NOTES: NoteMapItem[] = [];

/** Zoom applied when flying to an artifact / the viewer's location. */
const ZOOM_TO_ARTIFACT_LEVEL = 15;
/** Camera animation duration for reset / zoom-to flights. */
const FLY_TO_DURATION_MS = 1000;
/**
 * Delay before a zoom-to flight starts (mirrors the authenticated
 * `performZoomToCoordinates`). Lets a touch gesture settle so a `flyTo` issued
 * from a mobile task-marker `touchend` isn't cancelled by the gesture tail.
 */
const ZOOM_TO_ARTIFACT_DELAY_MS = 400;
/** Zoom applied after picking an address-search result (clamped to the share's max). */
const ADDRESS_SEARCH_ZOOM = MAP_CONFIG.maxZoom - 2;

export interface PublicMapShellState {
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
  stableZoom: number;
  mapStyle: string | StyleSpecification;
  initialViewState: { longitude: number; latitude: number; zoom: number };
  /** Share-configured camera bounds (fallback: global MAP_CONFIG). */
  effectiveMinZoom: number;
  effectiveMaxZoom: number;
  /** Stable [lng, lat] tuple for the toolbar's reset-view affordance. */
  resetCenter: [number, number];
  /** Lift the bottom controls toolbar above the mobile detail panel. */
  isAbovePreviewPanel: boolean;
  isAboveMinimizedPanel: boolean;
  visibleTasks: Task[];
  visibleNotes: NoteMapItem[];
  anyArtifactVisible: boolean;
  roadLayerVisibility: LayerVisibility;
  hasRoadLayers: boolean;
  handleMapLoad: () => void;
  handleMove: (event: ViewStateChangeEvent) => void;
  handleResetView: () => void;
  handleAddressSelect: (lat: number, lon: number) => void;
}

export function usePublicMapShellState(
  token: string,
  config: PublicShareConfig,
  initialBasemapId?: string
): PublicMapShellState {
  const { isMobile } = useDeviceLayout();
  const mapRef = useRef<MapRef | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  // Shares created before min/max zoom existed fall back to the global bounds.
  const effectiveMinZoom = config.minZoom ?? MAP_CONFIG.minZoom;
  const effectiveMaxZoom = config.maxZoom ?? MAP_CONFIG.maxZoom;

  const initialViewState = useMemo(
    () => ({
      longitude: config.centerLng ?? MAP_CONFIG.center[0],
      latitude: config.centerLat ?? MAP_CONFIG.center[1],
      zoom: clampZoom(
        config.zoom ?? MAP_CONFIG.zoom,
        effectiveMinZoom,
        effectiveMaxZoom
      ),
    }),
    [
      config.centerLat,
      config.centerLng,
      config.zoom,
      effectiveMinZoom,
      effectiveMaxZoom,
    ]
  );

  // Stable [lng, lat] tuple — an inline literal on the consumer would create a
  // new array per render and re-subscribe the toolbar's moveend listener.
  const resetCenter = useMemo<[number, number]>(
    () => [initialViewState.longitude, initialViewState.latitude],
    [initialViewState.longitude, initialViewState.latitude]
  );

  // stableZoom for geometry renderers — only changes at 0.5 increments.
  const { stableZoom, updateStableZoom } = useStableZoom(initialViewState.zoom);

  const mapStyle = useTerrainMode(mapRef, isMapReady);
  useMapMarkerHoverPreview({ mapRef, isMapReady });

  const mapTasks = useGeoMarkupStore(useShallow(selectMapTasks));
  const mapNotes = useGeoMarkupStore(useShallow(selectMapNotes));
  const artifactTasksVisible = useGeoMarkupStore(selectArtifactTasksVisible);
  const artifactNotesVisible = useGeoMarkupStore(selectArtifactNotesVisible);
  const artifactObstructionsVisible = useGeoMarkupStore(
    selectArtifactObstructionsVisible
  );
  const standardLayerVisibility = useGeoMarkupStore(
    useShallow((state) => state.mapLayers.standardLayerVisibility)
  );
  const zoomToCoordinates = useGeoMarkupStore(
    (state) => state.map.zoomToCoordinates
  );
  const zoomToBottomPadding = useGeoMarkupStore(
    (state) => state.map.zoomToBottomPadding
  );
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);

  // Lift the bottom controls toolbar above the mobile detail panel, mirroring
  // the authenticated map. `previewMode` is only ever set by the detail panel
  // (useMobilePreviewMode), so it already implies an open panel — no extra
  // "is panel open" guard is needed here (selectIsAnyArtifactPanelOpen is always
  // false on the public map, where the panel is URL-driven, not slice-driven).
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const isMobilePanelMinimized = useGeoMarkupStore(
    (state) => state.ui.slidePanel.isMinimized
  );

  // Seed + persist the viewer's basemap pick per share (localStorage only —
  // public viewers have no server-side user settings).
  useShareBasemapPersistence(token, config.allowedBasemaps, initialBasemapId);

  // Store-driven zoom requests (go-to-my-location, artifact list rows, map
  // markers). `zoomToBottomPadding` frames the target above the mobile preview
  // sheet (parity with the authenticated MapContainer).
  useZoomToCoordinatesEffect({
    mapRef,
    isMapReady,
    zoomToCoordinates,
    zoom: ZOOM_TO_ARTIFACT_LEVEL,
    durationMs: FLY_TO_DURATION_MS,
    bottomPaddingPx: zoomToBottomPadding,
    delayMs: ZOOM_TO_ARTIFACT_DELAY_MS,
    onApplied: () => zoomTo(null),
  });

  // Road layers render only when the share explicitly lists them; the
  // viewer's per-visit toggles can then hide them again.
  const roadLayerVisibility = useMemo<LayerVisibility>(() => {
    const allowed = new Set(config.visibleLayers);
    return standardLayerKeys().reduce((visibility, key) => {
      visibility[key] = allowed.has(key) && standardLayerVisibility[key];
      return visibility;
    }, {} as LayerVisibility);
  }, [config.visibleLayers, standardLayerVisibility]);

  const hasRoadLayers = useMemo(
    () => standardLayerKeys().some((key) => config.visibleLayers.includes(key)),
    [config.visibleLayers]
  );

  const handleMapLoad = useCallback(() => setIsMapReady(true), []);

  const handleMove = useCallback(
    (event: ViewStateChangeEvent) => updateStableZoom(event.viewState.zoom),
    [updateStableZoom]
  );

  const handleResetView = useCallback(() => {
    mapRef.current?.flyTo({
      center: [initialViewState.longitude, initialViewState.latitude],
      zoom: initialViewState.zoom,
      pitch: 0,
      bearing: 0,
      duration: FLY_TO_DURATION_MS,
    });
  }, [initialViewState]);

  const handleAddressSelect = useCallback(
    (lat: number, lon: number) => {
      mapRef.current?.flyTo({
        center: [lon, lat],
        zoom: clampZoom(
          ADDRESS_SEARCH_ZOOM,
          effectiveMinZoom,
          effectiveMaxZoom
        ),
        duration: FLY_TO_DURATION_MS,
      });
    },
    [effectiveMinZoom, effectiveMaxZoom]
  );

  return {
    mapRef,
    isMapReady,
    stableZoom,
    mapStyle,
    initialViewState,
    effectiveMinZoom,
    effectiveMaxZoom,
    resetCenter,
    isAbovePreviewPanel: isMobile && previewMode && !isMobilePanelMinimized,
    isAboveMinimizedPanel: isMobile && isMobilePanelMinimized,
    visibleTasks: artifactTasksVisible ? mapTasks : EMPTY_TASKS,
    visibleNotes: artifactNotesVisible ? mapNotes : EMPTY_NOTES,
    anyArtifactVisible:
      artifactTasksVisible ||
      artifactNotesVisible ||
      artifactObstructionsVisible,
    roadLayerVisibility,
    hasRoadLayers,
    handleMapLoad,
    handleMove,
    handleResetView,
    handleAddressSelect,
  };
}
